import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, MessageCircle, Phone, Mail, X, DollarSign, Save, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { id: 'novo', label: 'Novo', color: 'border-[#22D3EE]/30 bg-[#22D3EE]/5' },
  { id: 'em_analise', label: 'Em Análise', color: 'border-blue-500/30 bg-blue-500/5' },
  { id: 'orcamento_enviado', label: 'Orçamento Enviado', color: 'border-[#4F46E5]/30 bg-[#4F46E5]/5' },
  { id: 'negociando', label: 'Negociando', color: 'border-amber-500/30 bg-amber-500/5' },
  { id: 'fechado', label: 'Fechado', color: 'border-emerald-500/30 bg-emerald-500/5' },
  { id: 'perdido', label: 'Perdido', color: 'border-red-500/30 bg-red-500/5' },
];

export default function KanbanPipeline() {
  const { getAuthHeader } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const loadQuotations = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quotations`, {
        headers: getAuthHeader(), withCredentials: true,
      });
      setQuotations(data);
    } catch (e) { logger.error('Failed to load quotations:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadQuotations(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleDragStart = (e, quotationId) => {
    e.dataTransfer.setData('quotationId', quotationId);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDragOver(stageId);
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    setDragOver(null);
    const quotationId = e.dataTransfer.getData('quotationId');
    if (!quotationId) return;
    setQuotations(prev => prev.map(q => q.id === quotationId ? { ...q, status: stageId } : q));
    try {
      await axios.patch(`${API}/admin/quotations/${quotationId}`,
        { status: stageId },
        { headers: getAuthHeader(), withCredentials: true });
    } catch (err) {
      logger.error('Drop failed:', err);
      loadQuotations();
    }
  };

  const openCard = (q) => {
    setSelected(q);
    setEditValue(q.value ? String(q.value) : '');
    setEditNotes(q.notes || '');
    setEditStatus(q.status || 'novo');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        notes: editNotes,
        value: parseFloat(editValue) || 0,
        status: editStatus,
      };
      const { data } = await axios.patch(`${API}/admin/quotations/${selected.id}`,
        payload,
        { headers: getAuthHeader(), withCredentials: true });
      setQuotations(prev => prev.map(q => q.id === selected.id ? data : q));
      setSelected(null);
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const confirmed = window.confirm(
      `Mover o card de "${selected.name}" para a Lixeira?\n\nVocê poderá restaurá-lo depois acessando o menu Lixeira.`
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/quotations/${selected.id}`,
        { headers: getAuthHeader(), withCredentials: true });
      setQuotations(prev => prev.filter(q => q.id !== selected.id));
      setSelected(null);
    } catch (err) {
      alert('Erro ao mover para lixeira: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const openWhatsApp = (phone, name) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá ${name}! Aqui é da Done Technology. Recebemos seu pedido de orçamento e gostaríamos de conversar.`);
    window.open(`https://wa.me/55${cleanPhone}?text=${message}`, '_blank');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-[#22D3EE]" size={40} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="kanban-pipeline">
      <div>
        <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Pipeline Comercial</h1>
        <p className="text-[#94A3B8]">Arraste para mudar status ou clique no card para ver detalhes e definir o valor fechado</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 overflow-x-auto">
        {STAGES.map((stage) => {
          const stageQuotations = quotations.filter(q => q.status === stage.id);
          return (
            <div
              key={stage.id}
              data-testid={`kanban-column-${stage.id}`}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => handleDrop(e, stage.id)}
              className={`rounded-xl border ${stage.color} p-3 min-h-[500px] transition-all ${
                dragOver === stage.id ? 'ring-2 ring-[#22D3EE]' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-3 px-2">
                <h3 className="text-sm font-semibold text-[#F8FAFC]">{stage.label}</h3>
                <span className="text-xs text-[#94A3B8] bg-white/5 px-2 py-1 rounded">
                  {stageQuotations.length}
                </span>
              </div>

              <div className="space-y-2">
                {stageQuotations.map((q) => (
                  <motion.div
                    key={q.id}
                    layout
                    draggable
                    onDragStart={(e) => handleDragStart(e, q.id)}
                    onClick={() => openCard(q)}
                    data-testid={`kanban-card-${q.id}`}
                    className="bg-[#0d0f17] border border-white/10 hover:border-[#22D3EE]/40 rounded-lg p-3 cursor-pointer transition-all"
                  >
                    <p className="font-medium text-[#F8FAFC] text-sm mb-1">{q.name}</p>
                    {q.company && <p className="text-xs text-[#94A3B8] mb-2">{q.company}</p>}
                    <div className="flex items-center gap-1 mb-2 flex-wrap">
                      <span className="text-[10px] bg-[#22D3EE]/10 text-[#22D3EE] px-2 py-0.5 rounded">
                        {q.project_type}
                      </span>
                      <span className="text-[10px] bg-white/5 text-[#94A3B8] px-2 py-0.5 rounded">
                        {q.budget_range}
                      </span>
                    </div>
                    {q.value > 0 && (
                      <div className="mb-2 pb-2 border-b border-white/5">
                        <p className="text-sm font-semibold text-[#22D3EE]">
                          R$ {Number(q.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
                      <button
                        onClick={(e) => { e.stopPropagation(); openWhatsApp(q.phone, q.name); }}
                        data-testid={`whatsapp-${q.id}`}
                        className="p-1.5 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors"
                        title="WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </button>
                      <a
                        href={`mailto:${q.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded bg-[#22D3EE]/10 hover:bg-[#22D3EE]/20 text-[#22D3EE] transition-colors"
                        title="Email"
                      >
                        <Mail size={14} />
                      </a>
                      <a
                        href={`tel:${q.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded bg-[#4F46E5]/10 hover:bg-[#4F46E5]/20 text-[#4F46E5] transition-colors"
                        title="Ligar"
                      >
                        <Phone size={14} />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Detalhes */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelected(null)}
            data-testid="quotation-detail-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              className="bg-[#0d0f17] border border-[#22D3EE]/30 rounded-2xl p-6 w-full max-w-2xl my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#F8FAFC]">{selected.name}</h2>
                  {selected.company && <p className="text-[#94A3B8] text-sm">{selected.company}</p>}
                </div>
                <button onClick={() => setSelected(null)} className="text-[#94A3B8] hover:text-[#F8FAFC]"><X size={20} /></button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <p className="text-[#94A3B8] mb-1">Email</p>
                  <p className="text-[#F8FAFC]">{selected.email}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] mb-1">Telefone</p>
                  <p className="text-[#F8FAFC]">{selected.phone}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] mb-1">Tipo de Projeto</p>
                  <span className="text-xs bg-[#22D3EE]/10 text-[#22D3EE] px-2 py-1 rounded">{selected.project_type}</span>
                </div>
                <div>
                  <p className="text-[#94A3B8] mb-1">Orçamento Cliente</p>
                  <p className="text-[#F8FAFC]">{selected.budget_range}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] mb-1">Prazo</p>
                  <p className="text-[#F8FAFC]">{selected.deadline}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] mb-1">Criado em</p>
                  <p className="text-[#F8FAFC]">{new Date(selected.created_at).toLocaleString('pt-BR')}</p>
                </div>
              </div>

              {selected.features?.length > 0 && (
                <div className="mb-4">
                  <p className="text-[#94A3B8] mb-2 text-sm">Funcionalidades</p>
                  <div className="flex flex-wrap gap-1">
                    {selected.features.map((f) => (
                      <span key={f} className="text-xs bg-[#4F46E5]/10 text-[#4F46E5] px-2 py-1 rounded">{f}</span>
                    ))}
                  </div>
                </div>
              )}

              {selected.description && (
                <div className="mb-6">
                  <p className="text-[#94A3B8] mb-2 text-sm">Descrição do Cliente</p>
                  <p className="text-[#F8FAFC] text-sm bg-black/20 border border-white/5 rounded-lg p-3">{selected.description}</p>
                </div>
              )}

              <div className="border-t border-white/10 pt-5 space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-[#22D3EE] mb-2">
                    <DollarSign size={16} /> Valor Fechado (apenas admin)
                  </label>
                  <p className="text-xs text-[#94A3B8] mb-2">Quando o status estiver "Fechado", esse valor entra na Receita Total</p>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] text-sm">R$</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder="0,00"
                      data-testid="quotation-value-input"
                      className="w-full bg-[#080710] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#94A3B8] mb-2">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    data-testid="quotation-status-select"
                    className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                  >
                    {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#94A3B8] mb-2">Observações Internas</label>
                  <textarea
                    rows={4}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Anotações internas sobre este lead..."
                    data-testid="quotation-notes-input"
                    className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none resize-none"
                  />
                </div>

                <div className="flex gap-2 pt-3">
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    data-testid="delete-quotation-button"
                    className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-lg font-semibold hover:bg-red-500/20 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                    title="Excluir card do pipeline"
                  >
                    <Trash2 size={16} />
                  </button>
                  <button
                    onClick={() => openWhatsApp(selected.phone, selected.name)}
                    className="flex-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 py-2.5 rounded-lg font-semibold hover:bg-emerald-500/20 transition-all flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-quotation-button"
                    className="flex-1 bg-[#22D3EE] text-[#080710] py-2.5 rounded-lg font-semibold hover:shadow-[0_0_20px_rgba(34,211,238,0.5)] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
